import React, { useState, useEffect } from 'react';
import { Calendar, Link as LinkIcon, Unlink, CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';

const GoogleCalendarSettings = () => {
  const { adminToken } = useAdminAuth();
  const { toast } = useToast();
  const [isConnected, setIsConnected] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (adminToken) {
      checkConnectionStatus();
    }
    
    // Check for OAuth callback success/error in URL
    const params = new URLSearchParams(window.location.search);
    if (params.get('calendar_connected') === 'true') {
      toast({
        title: "Google Calendar collegato!",
        description: "Ora i follow-up verranno aggiunti automaticamente al tuo calendario"
      });
      // Remove param from URL
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('calendar_error') === 'true') {
      toast({
        title: "Errore collegamento",
        description: "Si è verificato un errore nel collegamento a Google Calendar",
        variant: "destructive"
      });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [adminToken]);

  const checkConnectionStatus = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/status`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setIsConnected(data.is_connected);
        setUserEmail(data.user_email);
      }
    } catch (error) {
      console.error('Error checking calendar status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setActionLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/connect`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Redirect to Google OAuth
        window.location.href = data.authorization_url;
      } else {
        throw new Error('Failed to initiate OAuth');
      }
    } catch (error) {
      console.error('Error connecting calendar:', error);
      toast({
        title: "Errore",
        description: "Impossibile avviare il collegamento a Google Calendar",
        variant: "destructive"
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Vuoi davvero disconnettere Google Calendar? I follow-up non verranno più aggiunti automaticamente al calendario.')) {
      return;
    }

    try {
      setActionLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/disconnect`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        setIsConnected(false);
        setUserEmail(null);
        toast({
          title: "Disconnesso",
          description: "Google Calendar è stato disconnesso"
        });
      } else {
        throw new Error('Failed to disconnect');
      }
    } catch (error) {
      console.error('Error disconnecting calendar:', error);
      toast({
        title: "Errore",
        description: "Impossibile disconnettere Google Calendar",
        variant: "destructive"
      });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-4">
          <Calendar className="w-6 h-6 text-blue-600" />
          <h3 className="text-lg font-semibold">Google Calendar</h3>
        </div>
        <p className="text-gray-500">Caricamento...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center gap-3 mb-4">
        <Calendar className="w-6 h-6 text-blue-600" />
        <h3 className="text-lg font-semibold">Google Calendar</h3>
      </div>

      <p className="text-gray-600 mb-6">
        Collega il tuo Google Calendar per creare automaticamente eventi quando programmi un follow-up CRM.
      </p>

      {isConnected ? (
        <div className="space-y-4">
          {/* Connected Status */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="font-medium text-green-900">Collegato</span>
            </div>
            {userEmail && (
              <p className="text-sm text-green-700">Account: {userEmail}</p>
            )}
          </div>

          {/* Features */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Funzionalità attive:</h4>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Eventi creati automaticamente quando programmi un follow-up</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Promemoria configurati (15 min prima via popup, 1h prima via email)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Dettagli cliente e nota inclusi nella descrizione evento</span>
              </li>
            </ul>
          </div>

          {/* Disconnect Button */}
          <button
            onClick={handleDisconnect}
            disabled={actionLoading}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            <Unlink className="w-4 h-4" />
            {actionLoading ? 'Disconnessione...' : 'Disconnetti Google Calendar'}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Not Connected Status */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-5 h-5 text-gray-600" />
              <span className="font-medium text-gray-900">Non collegato</span>
            </div>
            <p className="text-sm text-gray-600">
              Collega Google Calendar per automatizzare la creazione di eventi per i tuoi follow-up.
            </p>
          </div>

          {/* Benefits */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Cosa otterrai:</h4>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Eventi Google Calendar creati automaticamente</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Notifiche prima dei follow-up (email e popup)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Sincronizzazione automatica con tutti i tuoi dispositivi</span>
              </li>
            </ul>
          </div>

          {/* Connect Button */}
          <button
            onClick={handleConnect}
            disabled={actionLoading}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium"
          >
            <LinkIcon className="w-5 h-5" />
            {actionLoading ? 'Collegamento in corso...' : 'Collega Google Calendar'}
            <ExternalLink className="w-4 h-4" />
          </button>

          <p className="text-xs text-gray-500">
            Verrai reindirizzato a Google per autorizzare l'accesso al tuo calendario
          </p>
        </div>
      )}
    </div>
  );
};

export default GoogleCalendarSettings;
